import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Seo } from "../../lib/seo.js";
import {
  initAudio,
  playBustThud,
  playDoorSwing,
  playKeyClick,
  playLockCrack,
  playWinSting,
  setSoundMuted,
} from "../../lib/sound.js";
import { Dust, Grain, LightCone, Vignette } from "./core.js";
import { TileGrid, type TileState } from "./Tile.js";
import {
  BetInput,
  FairnessFooter,
  MineCountPicker,
  MultiplierDisplay,
  MuteToggle,
  PrimaryCta,
  type Scene,
  SectionHeader,
  Stat,
  StatusPill,
  Wallet,
} from "./Controls.js";
import { AlarmOverlay, RewardCard } from "./Overlays.js";
import { FairnessModal } from "./FairnessModal.js";
import {
  isMine,
  multiplierAfter,
  rollMines,
  type RoundSpec,
} from "./engine.js";
import { rgs, setRgsAuthToken } from "../../lib/rgs-client.js";
import { useGame } from "../../store.js";
import { track } from "../../lib/analytics.js";

/**
 * Pressure Plate — a Mines variant played on a 5×5 vault floor.
 *
 * Today this runs entirely client-side: the engine sim derives mine
 * positions from a local server-seed/client-seed/nonce triple, and the
 * balance lives in component state. When the backend RGS routes ship,
 * `placeBet` and `pickTile` swap from local sim to /rgs/pressure-plate/*
 * calls without any UI changes — the state shape is already aligned with
 * the eventual server response.
 */

type Phase = "idle" | "midround" | "bust" | "win";

interface RoundState {
  spec: RoundSpec;
  bet: number;
  picks: number[]; // flat indices clicked, in pick order
  busted: boolean;
  bustedAt: number | null; // flat index that triggered the alarm
  cashedOut: boolean;
  cashoutMultiplier: number | null;
}

interface HistoryEntry {
  n: string; // round number
  m: number; // multiplier achieved (0 if bust)
  win: boolean;
  bet: number;
}

const DEFAULT_BET = 0.001;
const DEFAULT_MINES = 5;

// Recent rounds populate from session-local history; we render up to 10.
type RecentRound = HistoryEntry;

export function PressurePlate() {
  // ---- session bootstrap from /rgs/session/launch -------------------------
  // Replaces the previous client-only commitSeed + STARTING_BALANCE demo. The
  // operator iframe drives currency/balance via ?operator= + ?currency= URL
  // params, mirroring GameView's bootstrap.
  const sessionId = useGame((s) => s.sessionId);
  const balance = useGame((s) => s.balance);
  const currency = useGame((s) => s.currency);
  const sessionServerSeedHash = useGame((s) => s.serverSeedHash);
  const sessionClientSeed = useGame((s) => s.clientSeed);
  const operatorId = useGame((s) => s.operatorId);
  const locale = useGame((s) => s.locale);
  const storeGameSlug = useGame((s) => s.gameSlug);
  const hydrateFromLaunch = useGame((s) => s.hydrateFromLaunch);
  const setBalance = useGame((s) => s.setBalance);
  const setOperator = useGame((s) => s.setOperator);
  const clearSession = useGame((s) => s.clearSession);
  const refillDemoBalance = useGame((s) => s.refillDemoBalance);

  // Boot spinner — visible from mount until /rgs/session/launch resolves.
  const [booting, setBooting] = useState(true);
  // Inline error for insufficient-balance attempts. Cleared by the timer or
  // by the player tapping refill. Was a silent no-op before — players had
  // no way to know why DEPLOY did nothing.
  const [insufficientNotice, setInsufficientNotice] = useState(false);

  // Collapse the side rails below 760px — the fixed 240/240 frame plus
  // 60px tiles overflowed phones in landscape and clipped the CTA.
  const compact = useMediaQuery("(max-width: 760px)");

  // Per-round seed/nonce — server returns these on /rgs/round/open. The
  // serverSeed is revealed at open for PP/CC so the client can render
  // immediate per-tile feedback (see contract comment on OpenRoundResponse).
  const [roundIdState, setRoundIdState] = useState<string | null>(null);
  const [roundServerSeed, setRoundServerSeed] = useState<string | null>(null);
  const [roundClientSeed, setRoundClientSeed] = useState<string | null>(null);
  const [roundNonce, setRoundNonce] = useState<number>(0);
  const [roundServerSeedHash, setRoundServerSeedHash] = useState<string | null>(null);

  const [muted, setMuted] = useState(false);

  // Mute sync — same pattern as GameView.
  useEffect(() => {
    setSoundMuted(muted);
  }, [muted]);

  // Mirror the session JWT into the rgs-client so /rgs/round/open and
  // /rgs/round/resolve carry Authorization: Bearer <jwt>. Without this,
  // launch succeeds but every subsequent round-route returns 401.
  // (GameView wires this for the crash game; PP needs its own copy because
  // each iframe has its own rgs-client module instance.)
  const sessionToken = useGame((s) => s.token);
  useEffect(() => {
    setRgsAuthToken(sessionToken);
  }, [sessionToken]);

  // Bootstrap a Pressure Plate session on mount (StrictMode fires twice; the
  // sessionId guard prevents a 2nd launch). Reads operator/currency from the
  // URL so iframe parents can override.
  // If the player navigated here from /play (vault-breaker) the store still
  // holds a session bound to that slug — server 400s on slug mismatch on
  // every /rgs/round/open call. Wipe and re-launch as pressure-plate.
  useEffect(() => {
    if (sessionId && storeGameSlug === "pressure-plate") {
      setBooting(false);
      return;
    }
    if (sessionId && storeGameSlug !== "pressure-plate") {
      clearSession();
    }
    const params = new URLSearchParams(location.search);
    const opId = params.get("operator") ?? "demo";
    const loc = params.get("locale") ?? locale;
    const cur =
      (params.get("currency") as "BTC" | "ETH" | "SOL" | "USDT" | "USD" | null) ?? "BTC";
    setOperator(opId, loc);
    rgs
      .launch({
        operatorId: opId,
        playerId: `demo_${Math.random().toString(36).slice(2, 8)}`,
        currency: cur,
        locale: loc,
        gameSlug: "pressure-plate",
      })
      .then((res) => {
        hydrateFromLaunch(res);
        setBooting(false);
        track("session_launched", { gameSlug: "pressure-plate" });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("pressure-plate launch failed", err);
        setBooting(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local-only history of resolved rounds (populated from server responses).
  // The cross-session live history can be wired through /public/feed later.
  const [history, setHistory] = useState<RecentRound[]>([]);

  // ---- round-level state ----
  const [bet, setBet] = useState(DEFAULT_BET);
  const [mineCount, setMineCount] = useState(DEFAULT_MINES);
  const [round, setRound] = useState<RoundState | null>(null);
  const [fairOpen, setFairOpen] = useState(false);

  // Phase derives from round state
  const phase: Phase = !round
    ? "idle"
    : round.cashedOut
      ? "win"
      : round.busted
        ? "bust"
        : "midround";

  // ---- derived round metrics ----
  const safePicks = round ? round.picks.length - (round.busted ? 1 : 0) : 0;
  const currentMultiplier = useMemo(() => {
    if (!round) return 1;
    if (round.busted) return 0;
    if (round.cashedOut) return round.cashoutMultiplier ?? 1;
    return multiplierAfter(safePicks, round.spec.mineCount);
  }, [round, safePicks]);

  const projectedPayout = bet * currentMultiplier;

  // ---- tile state matrix derived from picks ----
  const { tileMatrix, mineMatrix } = useMemo(() => {
    const tileState: TileState[][] = Array.from({ length: 5 }, () =>
      Array<TileState>(5).fill("hidden"),
    );
    const mineMx: boolean[][] = Array.from({ length: 5 }, () => Array(5).fill(false));

    if (!round) return { tileMatrix: tileState, mineMatrix: mineMx };

    const minePositions = rollMines(round.spec);
    const mineSet = new Set(minePositions);

    if (phase === "bust" || phase === "win") {
      for (let r = 0; r < 5; r += 1) {
        const mineRow = mineMx[r]!;
        const tileRow = tileState[r]!;
        for (let c = 0; c < 5; c += 1) {
          const idx = r * 5 + c;
          mineRow[c] = mineSet.has(idx);
          if (round.picks.includes(idx)) {
            tileRow[c] = mineSet.has(idx) ? "revealed-mine" : "revealed-safe";
          } else {
            tileRow[c] = "bust-other";
          }
        }
      }
    } else {
      for (const idx of round.picks) {
        const r = Math.floor(idx / 5);
        const c = idx % 5;
        tileState[r]![c] = mineSet.has(idx) ? "revealed-mine" : "revealed-safe";
      }
    }
    return { tileMatrix: tileState, mineMatrix: mineMx };
  }, [round, phase]);

  // ---- handlers ----------------------------------------------------------
  // DEPLOY → /rgs/round/open. Server commits seed, returns roundId + revealed
  // serverSeed (for client-side immediate feedback during play). The server
  // re-validates picks on /rgs/round/resolve so the wallet stays authoritative.
  const deploy = useCallback(async () => {
    if (!sessionId) return;
    if (!Number.isFinite(bet) || bet <= 0 || bet > balance) return;
    if (!Number.isInteger(mineCount) || mineCount < 1 || mineCount > 24) return;
    // Browser autoplay policy: AudioContext must be unlocked from a user
    // gesture. DEPLOY is the first guaranteed click; subsequent calls no-op.
    initAudio();
    playKeyClick();
    try {
      const res = await rgs.open({ sessionId, betAmount: bet, mineCount });
      // Server already debited the bet and returned the new balance.
      setBalance(res.balance);
      // We need the revealed seed to render per-tile feedback during play.
      // The server's resolve handler re-derives the same layout and
      // authoritatively settles the round, so the client can't "cheat" into
      // a payout — it can only see mine positions early. (See contract
      // comment on OpenRoundResponse.serverSeed for the trade-off.)
      if (!res.serverSeed) {
        // Defensive: server should always return a seed for PP — if it
        // doesn't, abort the round rather than play blind.
        // eslint-disable-next-line no-console
        console.error("pressure-plate open: missing serverSeed in response");
        return;
      }
      setRoundIdState(res.roundId);
      setRoundServerSeed(res.serverSeed);
      setRoundClientSeed(res.clientSeed);
      setRoundNonce(res.nonce);
      setRoundServerSeedHash(res.serverSeedHash);
      setRound({
        spec: {
          serverSeed: res.serverSeed,
          clientSeed: res.clientSeed,
          nonce: res.nonce,
          mineCount,
        },
        bet,
        picks: [],
        busted: false,
        bustedAt: null,
        cashedOut: false,
        cashoutMultiplier: null,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("pressure-plate open failed", err);
    }
  }, [sessionId, bet, balance, mineCount, setBalance]);

  // Helper: tell the server the round is over. Uses the discriminated-union
  // ResolveRoundRequest with gameSlug="pressure-plate". On a bust we flag
  // cashedOut=false; on a manual or auto cashout we flag cashedOut=true. The
  // server walks tilesRevealed, finds the first mine (if any), and pays out.
  const resolveRound = useCallback(
    async (
      tilesRevealed: number[],
      cashedOut: boolean,
      finalMultiplier: number,
    ): Promise<void> => {
      if (!sessionId || !roundIdState) return;
      try {
        const res = await rgs.resolve({
          sessionId,
          roundId: roundIdState,
          gameSlug: "pressure-plate",
          tilesRevealed,
          cashedOut,
        });
        setBalance(res.balance);
        // Append to local history so the right-rail card has data; trim to
        // the last 10 to keep the rail tight.
        setHistory((prev) =>
          [
            {
              n: `${(48270 + roundNonce).toLocaleString()}`,
              m: res.outcome === "win" ? finalMultiplier : 0,
              win: res.outcome === "win",
              bet,
            },
            ...prev,
          ].slice(0, 10),
        );
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("pressure-plate resolve failed", err);
      }
    },
    [sessionId, roundIdState, roundNonce, bet, setBalance],
  );

  const pickTile = useCallback(
    (row: number, col: number) => {
      if (!round || round.busted || round.cashedOut) return;
      const idx = row * 5 + col;
      if (round.picks.includes(idx)) return;

      // Local validation against the server-revealed seed. Server still
      // re-validates on resolve, so this can't shift the payout — it just
      // drives the immediate visual.
      const hit = isMine(round.spec, idx);
      if (hit) {
        playBustThud();
        setTimeout(playLockCrack, 80);
      } else {
        playKeyClick();
      }
      // Compute the next-state picks and decide whether the round is over.
      const nextPicks = [...round.picks, idx];
      if (hit) {
        // Bust — tell the server. resolveRound() awaits but state setters
        // fire synchronously so the UI flips to bust immediately.
        setRound({ ...round, picks: nextPicks, busted: true, bustedAt: idx });
        void resolveRound(nextPicks, false, 0);
        return;
      }
      const safeRemaining = 25 - round.spec.mineCount - nextPicks.length;
      if (safeRemaining === 0) {
        // Auto-cashout on full clear.
        const finalMult = multiplierAfter(nextPicks.length, round.spec.mineCount);
        playWinSting();
        setTimeout(playDoorSwing, 120);
        setRound({
          ...round,
          picks: nextPicks,
          cashedOut: true,
          cashoutMultiplier: finalMult,
        });
        void resolveRound(nextPicks, true, finalMult);
        return;
      }
      setRound({ ...round, picks: nextPicks });
    },
    [round, resolveRound],
  );

  const cashout = useCallback(() => {
    if (!round || round.busted || round.cashedOut || round.picks.length === 0) return;
    const mult = multiplierAfter(round.picks.length, round.spec.mineCount);
    initAudio();
    playWinSting();
    setTimeout(playDoorSwing, 120);
    setRound({ ...round, cashedOut: true, cashoutMultiplier: mult });
    void resolveRound(round.picks, true, mult);
  }, [round, resolveRound]);

  const reset = useCallback(() => {
    setRound(null);
    setRoundIdState(null);
    setRoundServerSeed(null);
    setRoundClientSeed(null);
    setRoundServerSeedHash(null);
  }, []);

  // ---- CTA configuration based on phase ---------------------------------
  const insufficientForBet = bet > balance;
  const triggerRefill = useCallback(() => {
    track("refill_demo_balance", { gameSlug: "pressure-plate" });
    setInsufficientNotice(false);
    void refillDemoBalance("pressure-plate");
  }, [refillDemoBalance]);

  let ctaLabel: string;
  let ctaSub: string | null;
  let ctaKind: "gold" | "green" | "ghost";
  let ctaDisabled = false;
  let ctaOnClick: () => void;
  if (phase === "idle") {
    if (insufficientForBet) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Bet ${bet.toFixed(8)} > balance ${balance.toFixed(8)}`;
      ctaKind = "gold";
      ctaDisabled = false;
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "DEPLOY";
      ctaSub = `${mineCount} mines · 25 plates`;
      ctaKind = "gold";
      ctaDisabled = bet <= 0;
      ctaOnClick = deploy;
    }
  } else if (phase === "midround") {
    if (safePicks === 0) {
      ctaLabel = "STEP CAREFULLY";
      ctaSub = "Pick a plate";
      ctaKind = "ghost";
      ctaDisabled = true;
      ctaOnClick = () => {};
    } else {
      ctaLabel = "CASH OUT";
      ctaSub = `@ ${currentMultiplier.toFixed(2)}× · +${(projectedPayout - bet).toFixed(8)} ${currency}`;
      ctaKind = "green";
      ctaOnClick = cashout;
    }
  } else {
    // bust or win — DEPLOY AGAIN (or refill if balance ran out)
    if (insufficientForBet) {
      ctaLabel = "↻ REFILL DEMO BALANCE";
      ctaSub = `Balance ${balance.toFixed(8)} ${currency}`;
      ctaKind = "gold";
      ctaOnClick = triggerRefill;
    } else {
      ctaLabel = "DEPLOY AGAIN";
      ctaSub = bet.toFixed(8);
      ctaKind = "gold";
      ctaOnClick = () => {
        reset();
        setTimeout(() => {
          if (bet <= balance) {
            deploy();
          } else {
            setInsufficientNotice(true);
          }
        }, 50);
      };
    }
  }

  // ---- render ------------------------------------------------------------
  const sceneForUi: Scene = phase;

  const stageHue = phase === "bust" ? "red" : "warm";

  return (
    <>
      <Seo
        title="Pressure Plate"
        description="A vault-floor Mines variant. Pick pressure plates that don't trip the silent alarm. Provably fair every round."
        path="/play/pressure-plate"
      />
      <div
        style={{
          position: "relative",
          minHeight: "100dvh",
          background: "var(--obsidian)",
          color: "var(--fg-1)",
          fontFamily: "var(--font-ui)",
          overflow: "hidden",
        }}
      >
        {/* Atmospherics */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
          <LightCone hue={stageHue} intensity={1} />
          <Vignette strength={1} />
          <Dust count={45} intensity={phase === "bust" ? 0.35 : 0.7} color={phase === "bust" ? "red" : "gold"} />
          <Grain opacity={0.06} />
        </div>

        <Layout
          phase={sceneForUi}
          mineCount={mineCount}
          bet={bet}
          balance={balance}
          muted={muted}
          booting={booting}
          compact={compact}
          insufficientNotice={insufficientNotice}
          onMuteToggle={() => setMuted((m) => !m)}
          onBetChange={setBet}
          onMineCountChange={setMineCount}
          tileState={tileMatrix}
          mineMx={mineMatrix}
          onTileClick={phase === "midround" ? pickTile : undefined}
          multiplier={currentMultiplier}
          picks={safePicks}
          ctaLabel={ctaLabel}
          ctaSub={ctaSub}
          ctaKind={ctaKind}
          ctaDisabled={ctaDisabled}
          ctaOnClick={ctaOnClick}
          serverHash={
            // Display the active round's hash when one is open; otherwise
            // fall back to the session's hash from launch. Both are real
            // server-committed values now.
            roundServerSeedHash
              ? `0x${roundServerSeedHash.slice(0, 4)}…${roundServerSeedHash.slice(-4)}`
              : sessionServerSeedHash
                ? `0x${sessionServerSeedHash.slice(0, 4)}…${sessionServerSeedHash.slice(-4)}`
                : "—"
          }
          clientSeed={roundClientSeed ?? sessionClientSeed ?? "—"}
          nonce={roundNonce}
          history={history}
          onVerify={() => setFairOpen(true)}
        />

        {phase === "bust" && (
          <AlarmOverlay visible mineCount={mineCount} bet={bet} currency={currency} />
        )}
        {phase === "win" && round && (
          <RewardCard
            visible
            multiplier={round.cashoutMultiplier ?? currentMultiplier}
            payout={bet * (round.cashoutMultiplier ?? currentMultiplier) - bet}
            currency={currency}
            mineCount={mineCount}
            picks={safePicks}
          />
        )}

        <FairnessModal
          open={fairOpen}
          onClose={() => setFairOpen(false)}
          serverSeedHash={roundServerSeedHash ?? sessionServerSeedHash ?? ""}
          // Server only reveals the seed for the ACTIVE round (PP/CC reveal
          // at open). The verify modal still shows it; if no round is open,
          // it stays empty so the player knows nothing is verifiable yet.
          serverSeed={roundServerSeed ?? ""}
          clientSeed={roundClientSeed ?? sessionClientSeed ?? ""}
          nonce={roundNonce}
          mineCount={round ? round.spec.mineCount : mineCount}
          picks={round ? round.picks : []}
          outcome={phase}
        />
      </div>
    </>
  );
}

// ===========================================================================
// Layout — separated to keep PressurePlate.tsx focused on state.
// ===========================================================================

interface LayoutProps {
  phase: Scene;
  mineCount: number;
  bet: number;
  balance: number;
  muted: boolean;
  booting: boolean;
  compact: boolean;
  insufficientNotice: boolean;
  onMuteToggle: () => void;
  onBetChange: (v: number) => void;
  onMineCountChange: (v: number) => void;
  tileState: TileState[][];
  mineMx: boolean[][];
  onTileClick?: (row: number, col: number) => void;
  multiplier: number;
  picks: number;
  ctaLabel: string;
  ctaSub: string | null;
  ctaKind: "gold" | "green" | "ghost";
  ctaDisabled: boolean;
  ctaOnClick: () => void;
  serverHash: string;
  clientSeed: string;
  nonce: number;
  history: HistoryEntry[];
  onVerify: () => void;
}

function Layout(p: LayoutProps) {
  // ---- Keyboard navigation ------------------------------------------------
  // The footer hint advertises ↑↓←→/Enter/Esc. Tile.tsx already handles
  // Enter/Space via its onKeyDown; this effect wires the arrow keys + Escape.
  // We attach to window (not main) so the listener fires regardless of which
  // descendant has focus, but bail out if the user is typing into an input.
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;

      // Esc cashes out when midround AND the CTA is in its "green" cashout
      // state (i.e. ≥1 safe pick). Anything else is a no-op.
      if (e.key === "Escape") {
        if (
          p.phase === "midround" &&
          p.picks > 0 &&
          !p.ctaDisabled &&
          p.ctaKind === "green"
        ) {
          e.preventDefault();
          p.ctaOnClick?.();
        }
        return;
      }

      if (
        e.key !== "ArrowUp" &&
        e.key !== "ArrowDown" &&
        e.key !== "ArrowLeft" &&
        e.key !== "ArrowRight"
      ) {
        return;
      }
      if (p.phase !== "midround") return;

      const dirs: Record<string, [number, number]> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const [dr, dc] = dirs[e.key]!;

      const active = document.activeElement as HTMLElement | null;
      const aRow = Number(active?.getAttribute("data-pp-row") ?? -1);
      const aCol = Number(active?.getAttribute("data-pp-col") ?? -1);
      const startRow = aRow >= 0 ? aRow : 0;
      const startCol = aCol >= 0 ? aCol : 0;

      // Step in direction; clamp at edges (no wrap on a 5×5). Skip revealed
      // tiles by stepping again — feels right because revealed tiles are
      // un-clickable anyway. Bail when the next step would leave the grid
      // so we don't focus a non-existent cell or fall through the loop.
      let nr = startRow + dr;
      let nc = startCol + dc;
      let foundHidden = false;
      while (nr >= 0 && nr <= 4 && nc >= 0 && nc <= 4) {
        const cell = p.tileState[nr]?.[nc];
        if (cell === "hidden") {
          foundHidden = true;
          break;
        }
        nr += dr;
        nc += dc;
      }
      if (!foundHidden) return;
      e.preventDefault();
      const next = document.querySelector<HTMLDivElement>(
        `[data-pp-row="${nr}"][data-pp-col="${nc}"]`,
      );
      next?.focus();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [p.phase, p.picks, p.ctaDisabled, p.ctaKind, p.ctaOnClick, p.tileState]);

  // ---- Auto-focus first hidden tile on round start ------------------------
  // Without this, the first arrow press has no anchor (document.activeElement
  // is body) and the user has to Tab into the grid first. We fire when the
  // round transitions into midround state.
  useEffect(() => {
    if (p.phase !== "midround") return;
    // Defer one frame so React commits the new tile DOM before we focus.
    const id = requestAnimationFrame(() => {
      for (let r = 0; r < 5; r += 1) {
        for (let c = 0; c < 5; c += 1) {
          if (p.tileState[r]?.[c] === "hidden") {
            const tile = document.querySelector<HTMLDivElement>(
              `[data-pp-row="${r}"][data-pp-col="${c}"]`,
            );
            tile?.focus();
            return;
          }
        }
      }
    });
    return () => cancelAnimationFrame(id);
    // Re-run only when phase flips into midround, not on every tile state
    // change — focusing on every pick would steal focus from the user's
    // actual click target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.phase]);

  // Tile size — design uses 110 on desktop, ~57 on mobile.
  // We use clamp() via inline style fallback: pick 96 default, the page itself
  // adjusts via media queries at the wrapper level.
  return (
    <div className="pp-shell" style={shellStyle}>
      {/* TOP BAR — operator-neutral. No studio branding so the iframe drops
           cleanly into any operator's lobby. Just the game name. */}
      <header style={topBarStyle}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 14,
            letterSpacing: "0.06em",
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Pressure Plate
        </span>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--fg-3)",
            }}
            className="pp-hide-mobile"
          >
            ROUND <span style={{ color: "var(--fg-1)" }}>#{(48271 + p.nonce).toLocaleString()}</span>
          </span>
          <MuteToggle muted={p.muted} onClick={p.onMuteToggle} />
          <Wallet balance={p.balance} />
        </div>
      </header>

      {/* GRID: left sidebar / center stage / right sidebar.
          Below 760px the side rails collapse out entirely (single-column)
          so the 60px tiles + CTA still fit a phone in landscape. */}
      <div
        className="pp-grid"
        style={p.compact ? { ...gridStyle, gridTemplateColumns: "1fr" } : gridStyle}
      >
        {/* LEFT — setup + stats. ROUND STATS is hidden in idle phase (all
            zeros pre-deploy is wasted ~160px of rail height). It re-appears
            during midround/win/bust when the values actually update. The
            "Setup" SectionHeader was redundant — the rail's only purpose
            pre-deploy is the bet+mine controls, so drop the extra header. */}
        {!p.compact && (
          <aside className="pp-sidebar pp-sidebar-left" style={leftPanelStyle}>
            <BetInput amount={p.bet} onChange={p.onBetChange} disabled={p.phase === "midround"} />
            <MineCountPicker
              value={p.mineCount}
              onChange={p.onMineCountChange}
              disabled={p.phase === "midround"}
              collapsed
            />

            {p.phase !== "idle" && (
              <>
                <div style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <Stat l="Picks" v={p.picks} />
                  <Stat
                    l="Multi"
                    v={`${p.multiplier.toFixed(2)}×`}
                    c={p.multiplier >= 2 ? "var(--gold-bright)" : "var(--fg-1)"}
                  />
                  <Stat
                    l="Profit"
                    v={`${p.picks > 0 && p.phase !== "bust" ? "+" : ""}${(p.bet * (p.multiplier - 1)).toFixed(8)}`}
                    c={p.multiplier > 1 ? "var(--green-bright)" : "var(--fg-3)"}
                  />
                  <Stat l="Mines" v={`${p.mineCount}/25`} />
                </div>
              </>
            )}
          </aside>
        )}

        {/* CENTER STAGE */}
        <main style={mainStyle}>
          {p.booting && (
            <div
              role="status"
              aria-live="polite"
              style={{
                display: "grid",
                placeItems: "center",
                gap: 12,
                padding: 32,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--fg-3)",
                letterSpacing: 1.5,
                textTransform: "uppercase",
              }}
            >
              <div
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "var(--gold-bright)",
                  boxShadow: "0 0 12px var(--gold-bright)",
                  animation: "pp-bump 900ms ease-in-out infinite",
                }}
              />
              <span>Arming pressure plates…</span>
            </div>
          )}
          {!p.booting && <StatusPill scene={p.phase} mineCount={p.mineCount} />}

          {/* Hide the live multiplier during win/bust — the overlay's giant
              multiplier is the focus there, no point in painting the same
              number twice. */}
          {!p.booting && p.phase !== "win" && p.phase !== "bust" && (
            <div style={{ minHeight: 64, display: "grid", placeItems: "center", marginTop: 8 }}>
              <MultiplierDisplay
                value={p.multiplier}
                mode={p.picks > 0 ? "live" : "idle"}
                size="sm"
                flashKey={p.picks}
              />
            </div>
          )}

          {!p.booting && (
            <div className="pp-grid-wrap" style={{ marginTop: 8 }}>
              <TileGrid
                state={p.tileState}
                mines={p.mineMx}
                size={p.compact ? 48 : 60}
                gap={4}
                glyphScale={0.9}
                onTileClick={p.onTileClick}
              />
            </div>
          )}

          {p.insufficientNotice && !p.booting && (
            <div
              role="alert"
              style={{
                marginTop: 10,
                padding: "8px 12px",
                background: "rgba(224,69,69,0.12)",
                border: "1px solid rgba(224,69,69,0.55)",
                borderRadius: 3,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--red-bright)",
                letterSpacing: 0.5,
              }}
            >
              Insufficient balance — refill your demo wallet to keep playing.
            </div>
          )}

          {!p.booting && (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--fg-3)",
                letterSpacing: 0.5,
                marginTop: 14,
                textAlign: "center",
                opacity: p.phase === "bust" || p.phase === "win" ? 0 : 1,
                transition: "opacity 200ms var(--ease-fade)",
              }}
            >
              {p.phase === "idle"
                ? "Place a bet. Choose mines. Step carefully."
                : `${p.picks} of ${25 - p.mineCount} safe plates cleared · multiplier rises with each step`}
            </div>
          )}
        </main>

        {/* RIGHT — round history (mock; sliced to 5 to keep the rail tight) */}
        {!p.compact && (
          <aside className="pp-sidebar pp-sidebar-right" style={rightPanelStyle}>
            <SectionHeader title="Last 5 Rounds" no="03" />
            <RoundList rounds={p.history.slice(0, 5)} />

            <div style={{ height: 1, background: "var(--line)", margin: "4px 0" }} />

            <SectionHeader title="Round Hash" no="04" />
            <HashCard serverHash={p.serverHash} clientSeed={p.clientSeed} nonce={p.nonce} />
          </aside>
        )}
      </div>

      {/* BOTTOM BAR — auto / 1fr / auto: fairness compresses, CTA grows, hints
          drop on narrow viewports via .pp-hide-mobile. */}
      <footer style={footerStyle}>
        <div className="pp-fairness-slot">
          <FairnessFooter onVerify={p.onVerify} />
        </div>
        <div className="pp-cta-slot" style={{ width: "100%" }}>
          <PrimaryCta
            label={p.ctaLabel}
            sub={p.ctaSub}
            kind={p.ctaKind}
            disabled={p.ctaDisabled}
            height={40}
            onClick={p.ctaOnClick}
          />
        </div>
        <KeyboardHints />
      </footer>
    </div>
  );
}

// ---- subcomponents -------------------------------------------------------

function RoundList({ rounds }: { rounds: HistoryEntry[] }) {
  if (rounds.length === 0) {
    return (
      <div
        style={{
          padding: "10px 10px",
          background: "rgba(17,21,31,0.4)",
          border: "1px dashed var(--line-bone)",
          borderRadius: 2,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--fg-4)",
          textAlign: "center",
          letterSpacing: 0.5,
        }}
      >
        No rounds yet · Deploy to start
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rounds.map((r) => (
        <div
          key={r.n}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 60px 70px",
            gap: 8,
            alignItems: "center",
            padding: "6px 10px",
            background: "rgba(17,21,31,0.5)",
            border: "1px solid var(--line-bone)",
            borderRadius: 2,
            fontFamily: "var(--font-mono)",
            fontSize: 11,
          }}
        >
          <span style={{ color: "var(--fg-3)" }}>#{r.n}</span>
          <span
            style={{
              textAlign: "right",
              color: r.win ? "var(--gold-bright)" : "var(--red-bright)",
              fontWeight: 600,
            }}
          >
            {r.m.toFixed(2)}×
          </span>
          <span
            style={{
              textAlign: "right",
              color: r.win ? "var(--green-bright)" : "var(--red)",
              fontSize: 10,
            }}
          >
            {r.win ? `+${(r.bet * (r.m - 1)).toFixed(5)}` : `−${r.bet.toFixed(5)}`}
          </span>
        </div>
      ))}
    </div>
  );
}

function HashCard({
  serverHash,
  clientSeed,
  nonce,
}: {
  serverHash: string;
  clientSeed: string;
  nonce: number;
}) {
  return (
    <div
      style={{
        background: "rgba(17,21,31,0.5)",
        border: "1px solid var(--line)",
        borderRadius: 2,
        padding: 10,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        lineHeight: 1.6,
        wordBreak: "break-all",
      }}
    >
      <div
        style={{
          color: "var(--fg-3)",
          fontSize: 9,
          letterSpacing: 1.5,
          fontFamily: "var(--font-ui)",
          fontWeight: 700,
          marginBottom: 6,
          textTransform: "uppercase",
        }}
      >
        Server seed hash
      </div>
      <div style={{ color: "var(--fg-2)" }}>{serverHash}</div>
      <div style={{ height: 1, background: "var(--line)", margin: "8px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: "var(--fg-3)" }}>nonce</span>
        <span style={{ color: "var(--cyan-bright)" }}>#{nonce}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ color: "var(--fg-3)" }}>client seed</span>
        <span style={{ color: "var(--fg-2)" }}>{clientSeed}</span>
      </div>
    </div>
  );
}

function KeyboardHints() {
  return (
    <div
      className="pp-hide-mobile"
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 12,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--fg-3)",
      }}
    >
      <Hint k="↑↓←→" l="navigate" />
      <Hint k="↵" l="step" />
      <Hint k="Esc" l="cash out" />
    </div>
  );
}
function Hint({ k, l }: { k: string; l: string }) {
  return (
    <span>
      <kbd
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          fontWeight: 600,
          padding: "2px 6px",
          background: "var(--bg-2)",
          border: "1px solid var(--line)",
          borderRadius: 2,
          color: "var(--gold-bright)",
          marginRight: 6,
        }}
      >
        {k}
      </kbd>
      <span style={{ color: "var(--fg-3)" }}>{l}</span>
    </span>
  );
}

// ---- helpers -------------------------------------------------------------

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

// ---- styles --------------------------------------------------------------

const shellStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  // 100dvh accounts for the iOS dynamic toolbar so the sticky CTA isn't
  // hidden under the URL bar; 100vh stays as the fallback for older browsers.
  minHeight: "100dvh",
  paddingBottom: "env(safe-area-inset-bottom)",
  zIndex: 1,
};

const topBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  height: 48,
  borderBottom: "1px solid var(--line)",
  background: "rgba(11,14,22,0.7)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  position: "sticky",
  top: 0,
  zIndex: 10,
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "240px 1fr 240px",
  gap: 0,
  flex: 1,
  minHeight: 0,
};

const leftPanelStyle: CSSProperties = {
  // Tighter padding/gap so the bet+mines+(stats) stack fits the iframe
  // budget on 720p without scroll. Was 12/10, now 10/6 — saves ~14px.
  padding: 10,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  background: "rgba(7,9,15,0.6)",
  borderRight: "1px solid var(--line)",
  // Allow the rail to shrink/scroll independently if the user lands on a
  // very short viewport; the sticky footer CTA stays visible regardless.
  minHeight: 0,
  overflowY: "auto",
};

const rightPanelStyle: CSSProperties = {
  padding: 10,
  display: "flex",
  flexDirection: "column",
  gap: 6,
  background: "rgba(7,9,15,0.6)",
  borderLeft: "1px solid var(--line)",
  minHeight: 0,
  overflowY: "auto",
};

const mainStyle: CSSProperties = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "10px 16px",
};

// Footer: fairness slot compresses to its own content (auto), CTA expands to
// claim the rest, keyboard hints sit on the far right and disappear at narrow
// viewports via .pp-hide-mobile. The old `1fr 420px 1fr` rigidly compressed
// the side slots below their min content width — auto/1fr/auto fixes that.
const footerStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  gap: 14,
  alignItems: "center",
  padding: "6px 16px",
  background: "rgba(11,14,22,0.85)",
  borderTop: "1px solid var(--line)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  position: "sticky",
  bottom: 0,
  zIndex: 10,
};
